/**
 * Account/membership enforcement — moderation status → capability override
 * (docs/105-ADMIN-MODERATION-AND-MEMBERSHIP-ENFORCEMENT.md v1). Mirrors the Worker's
 * `UserModeration` (worker/src/types.ts) so the app can feed `GET /auth/me`'s
 * `moderation` field straight into {@link AppCapabilities.resolve} (billing/capabilities.ts).
 *
 * - `"active"` / `"warned"` — full access; `"warned"` differs only in that the user
 *   also has an unread `Notice` in their inbox — this file makes no capability change
 *   for either.
 * - `"suspended"` — **read-only**: already-accessible content stays readable (the
 *   individually-granted `packs` list is untouched), but the blanket `member`/`teacher`
 *   ("membership perks") unlock is suppressed until `until` (epoch seconds; `null` =
 *   indefinite, until an explicit reinstate) — see {@link applyModerationOverride}.
 *   `AppCapabilities.canUseAuthoring` additionally forces authoring off outright (even
 *   for a role-granted author/admin account) while suspended — see capabilities.ts.
 * - `"banned"` — entitlements zero out entirely, but the *real* enforcement is the
 *   app's full lockout screen (`AppCapabilities.isBanned`, `app/banned.tsx`), since a
 *   free pack would otherwise still pass `canOpenPack`'s free-content rule.
 *
 * This is a **client-side mirror** of a decision the server already made — it never
 * substitutes for server enforcement (`GET /gated/*` still requires a valid content
 * token regardless of what a client renders); it just lets already-loaded screens
 * react to a status without a dedicated round trip.
 */
import { Entitlements } from "./entitlements";

export const MODERATION_STATUSES = ["active", "warned", "suspended", "banned"] as const;
export type ModerationStatus = (typeof MODERATION_STATUSES)[number];

export function isModerationStatus(x: unknown): x is ModerationStatus {
  return typeof x === "string" && (MODERATION_STATUSES as readonly string[]).includes(x);
}

/** Client-side shape of a moderation decision — mirrors the worker's `UserModeration`. */
export interface UserModeration {
  userId: string;
  status: ModerationStatus;
  reason?: string | null;
  until?: number | null;
  at: number;
  by: string;
}

/** The subset {@link applyModerationOverride}/{@link isModerationActive} actually need —
 * lets callers pass a bare `{ status, until }` without a full `UserModeration` row. */
export type ModerationState = Pick<UserModeration, "status" | "until">;

/** Default state for a user who has never been moderated (no stored row / `null` from `/auth/me`). */
export const ACTIVE_MODERATION: ModerationState = { status: "active", until: null };

/**
 * Whether `moderation`'s restriction is currently in effect at `now` (epoch seconds).
 * A `"suspended"` row past its `until` has lapsed and is treated the same as active —
 * this is what lets a time-boxed suspension self-expire without a reinstate call.
 */
export function isModerationActive(moderation: ModerationState | null | undefined, now: number): boolean {
  if (!moderation) return false;
  if (moderation.status === "banned") return true;
  if (moderation.status === "suspended") return moderation.until == null || now < moderation.until;
  return false; // active | warned
}

/**
 * Apply the moderation override to an already-effective entitlement set (i.e. after
 * `effectiveEntitlements`). Suspended suppresses the blanket `member`/`teacher` unlock
 * but keeps individually-granted `packs` (already-accessible content stays readable);
 * banned zeroes everything (paired with the app's lockout screen, not relied on alone
 * — see the module doc comment).
 */
export function applyModerationOverride(
  ent: Entitlements,
  moderation: ModerationState | null | undefined,
  now: number,
): Entitlements {
  if (!isModerationActive(moderation, now)) return ent;
  if (moderation!.status === "banned") return { member: false, teacher: false, packs: [] };
  return { ...ent, member: false, teacher: false }; // suspended: read-only, membership perks hidden
}
